import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { 
  PlayCircleIcon, 
  ShieldCheckIcon, 
  LockClosedIcon,
  CheckCircleIcon,
  MicrophoneIcon,
  VideoCameraIcon,
  ChatBubbleLeftRightIcon,
  SparklesIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline';
import ErrorAlert from '@/components/UI/ErrorAlert';
import { interviewApi } from '@/services/api';
import { resetInterviewState } from '@/store/slices/interviewSlice';

const Landing = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [sessionToken, setSessionToken] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleJoin = async (e) => {
    e.preventDefault();
    const token = sessionToken.trim();
    
    if (!token) {
      setError('Please enter a session token');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Validate token by fetching session info
      await interviewApi.getSessionInfo(token);
      // Clear any stale Redux state from a previous interview session
      dispatch(resetInterviewState());
      // Navigate — InterviewIntro will fetch fresh data
      navigate(`/interview/${token}`);
    } catch (err) {
      // Show error on landing page
      setError(err.message || 'Invalid session token. Please check and try again.');
      setLoading(false);
    }
  };

  const features = [
    {
      icon: VideoCameraIcon,
      title: 'Human Review Only',
      description: 'Your video is reviewed by real people, not analyzed by algorithms',
      color: 'sky'
    },
    {
      icon: ShieldCheckIcon,
      title: 'Privacy First',
      description: 'We never use AI to analyze body language or facial expressions',
      color: 'emerald'
    },
    {
      icon: CheckCircleIcon,
      title: 'Re-record Anytime',
      description: 'Answer on your own time with the flexibility to re-record if needed',
      color: 'emerald'
    }
  ];

  const steps = [
    {
      number: '01',
      title: 'Enter Your Token',
      description: 'Use the interview token from the email invitation you received',
      icon: LockClosedIcon
    },
    {
      number: '02',
      title: 'Record Your Responses',
      description: 'Answer interview questions on your own time with video. Re-record if needed.',
      icon: VideoCameraIcon
    },
    {
      number: '03',
      title: 'Submit & Finish',
      description: 'Your responses are securely saved and ready for review',
      icon: CheckCircleIcon
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-sky-50">
      {/* Navigation Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-lg shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 group">
              <div className="w-8 h-8 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-lg flex items-center justify-center shadow-lg">
                <SparklesIcon className="w-5 h-5 text-white" />
              </div>
              <span className="text-sm font-bold text-gray-900 tracking-tight hidden sm:inline">
                TeamSyncAI
              </span>
            </Link>
            
            {/* Help Link */}
            <Link
              to="/help"
              className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-indigo-600 transition-colors rounded-lg hover:bg-indigo-50"
              title="Help & Support"
            >
              <QuestionMarkCircleIcon className="w-5 h-5" />
              <span className="text-sm font-medium hidden sm:inline">Help</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-indigo-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-sky-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-purple-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-4000"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 sm:pt-20 sm:pb-24">
          {/* Logo and brand */}
          <div className="text-center mb-8 sm:mb-12">
            <div className="inline-flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg mb-6">
              <ShieldCheckIcon className="w-5 h-5 text-indigo-600" />
              <span className="text-sm font-semibold text-indigo-600 tracking-wide">
                PRIVATE & SECURE PLATFORM
              </span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
              Your Interview,
              <br />
              <span className="bg-gradient-to-r from-indigo-600 to-sky-500 bg-clip-text text-transparent">
                Your Way
              </span>
            </h1>
            
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto mb-2 sm:mb-4 px-4">
              Answer interview questions on your own time.
            </p>
            <p className="text-base sm:text-lg text-gray-500 max-w-2xl mx-auto mb-8 sm:mb-12 px-4">
              No scheduling. No pressure. Takes about 10–15 minutes.
            </p>

            {/* Error Alert - Show at top */}
            {error && (
              <div className="max-w-md mx-auto px-4 mb-8">
                <ErrorAlert 
                  message={error}
                  onClose={() => setError(null)}
                />
              </div>
            )}

            {/* Session Token Input - Hero CTA */}
            <form onSubmit={handleJoin} className="max-w-md mx-auto px-4">
              <div className="relative group">
                <div className={`absolute inset-0 bg-gradient-to-r from-indigo-500 to-sky-500 rounded-2xl blur-lg opacity-0 group-hover:opacity-30 transition-opacity duration-500 ${isFocused ? 'opacity-30' : ''}`}></div>
                
                <div className="relative bg-white rounded-2xl shadow-xl p-2 flex flex-col sm:flex-row gap-2">
                  <div className="flex-1 relative">
                    <LockClosedIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Enter token from your email invitation"
                      value={sessionToken}
                      onChange={(e) => setSessionToken(e.target.value)}
                      onFocus={() => setIsFocused(true)}
                      onBlur={() => setIsFocused(false)}
                      className="w-full pl-12 pr-4 py-4 text-base sm:text-lg rounded-xl border-0 focus:outline-none focus:ring-0 placeholder-gray-400 text-gray-900"
                      autoComplete="off"
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={!sessionToken.trim() || loading}
                    className="bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white font-semibold px-8 py-4 rounded-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 min-h-[56px] shadow-lg hover:shadow-xl transform hover:scale-105 active:scale-95"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        <span className="text-base sm:text-lg">Loading...</span>
                      </>
                    ) : (
                      <>
                        <PlayCircleIcon className="w-6 h-6" />
                        <span className="text-base sm:text-lg">Start Interview</span>
                      </>
                    )}
                  </button>
                </div>
              </div>

              <p className="mt-4 text-sm text-gray-500 flex items-center justify-center space-x-2">
                <ShieldCheckIcon className="w-4 h-4" />
                <span>Secure • Private</span>
              </p>
            </form>
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-8 mt-12 sm:mt-16 px-4">
            <div className="flex items-center space-x-2 text-gray-600">
              <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium">End-to-end encrypted</span>
            </div>
            <div className="flex items-center space-x-2 text-gray-600">
              <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium">No AI body language analysis</span>
            </div>
            <div className="flex items-center space-x-2 text-gray-600">
              <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium">Human-reviewed only</span>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="relative py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Simple Three-Step Process
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
              Getting started is easy. No downloads, no complicated setup.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {steps.map((step, index) => (
              <div 
                key={index}
                className="relative group"
              >
                {/* Connector line (hidden on mobile) */}
                {index < steps.length - 1 && (
                  <div className="hidden md:block absolute top-16 left-1/2 w-full h-0.5 bg-gradient-to-r from-indigo-200 to-sky-200 -z-10"></div>
                )}
                
                <div className="relative bg-gradient-to-br from-white to-gray-50 rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
                  {/* Step number */}
                  <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-indigo-600 to-sky-500 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-lg transform group-hover:scale-110 transition-transform">
                    {step.number}
                  </div>

                  {/* Icon */}
                  <div className="w-14 h-14 bg-indigo-100 rounded-xl flex items-center justify-center mb-6 mt-4">
                    <step.icon className="w-7 h-7 text-indigo-600" />
                  </div>

                  {/* Content */}
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Features / Trust Section */}
      <div className="relative py-16 sm:py-24 bg-gradient-to-br from-indigo-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <div className="inline-flex items-center space-x-2 bg-indigo-100 px-4 py-2 rounded-full mb-4">
              <ShieldCheckIcon className="w-5 h-5 text-indigo-600" />
              <span className="text-sm font-semibold text-indigo-600">YOUR PRIVACY MATTERS</span>
            </div>
            
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Transparent & Trustworthy
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              We believe in being upfront about how we handle your interview. 
              Here's exactly what happens with your data.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className={`w-14 h-14 bg-${feature.color}-100 rounded-xl flex items-center justify-center mb-6`}>
                  <feature.icon className={`w-7 h-7 text-${feature.color}-600`} />
                </div>
                
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>

          {/* Additional trust message */}
          <div className="mt-12 bg-white rounded-2xl p-6 sm:p-8 shadow-lg border-2 border-indigo-100">
            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-4">
              <div className="flex-shrink-0 w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                <CheckCircleIcon className="w-6 h-6 text-indigo-600" />
              </div>
              <div className="flex-1">
                <h4 className="text-lg font-bold text-gray-900 mb-1">
                  No AI Body Language Analysis
                </h4>
                <p className="text-gray-600">
                  Your video is reviewed by real people, not analyzed by AI. We never evaluate your body language, facial expressions, or tone. You're judged on your actual responses and qualifications.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile sticky CTA */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-2xl z-50">
        <button
          onClick={() => {
            const input = document.querySelector('input[type="text"]');
            if (input) {
              input.scrollIntoView({ behavior: 'smooth', block: 'center' });
              input.focus();
            }
          }}
          className="w-full bg-gradient-to-r from-indigo-600 to-indigo-700 text-white font-semibold px-6 py-4 rounded-xl flex items-center justify-center space-x-2 shadow-lg active:scale-95 transition-transform"
        >
          <PlayCircleIcon className="w-6 h-6" />
          <span>Get Started</span>
        </button>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes blob {
          0%, 100% { transform: translate(0, 0) scale(1); }
          25% { transform: translate(20px, -20px) scale(1.1); }
          50% { transform: translate(-20px, 20px) scale(0.9); }
          75% { transform: translate(20px, 20px) scale(1.05); }
        }
        
        .animate-blob {
          animation: blob 7s infinite;
        }
        
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        
        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default Landing;